/**
 * API Service
 * Main entry point for the API service
 */

// Export core components
export { ApiClient } from './ApiClient';
export * from './types';

// Export interceptors
export * from './interceptors/authInterceptor';
export * from './interceptors/loggingInterceptor';

// Export handlers
export * from './handlers/loadingHandlers';
export * from './handlers/notificationHandlers';

// Export hooks
export * from './hooks/useApi';

// Export RTK Query integration
export * from './rtkQuery/apiSlice';

// Import dependencies
import {ApiClient} from './ApiClient';
import {ApiClientConfig, AuthTokens} from './types';
import {
  createAuthErrorInterceptor,
  createAuthRequestInterceptor
} from './interceptors/authInterceptor';
import {
  createErrorLoggingInterceptor,
  createRequestLoggingInterceptor,
  createResponseLoggingInterceptor
} from './interceptors/loggingInterceptor';
import {
  createGlobalLoadingStateHandler,
  createLoadingStateManager
} from './handlers/loadingHandlers';
import {
  createErrorNotificationHandler,
  createSuccessNotificationHandler,
  NotificationConfig
} from './handlers/notificationHandlers';
import {createApiSlice} from './rtkQuery/apiSlice';

/**
 * Configuration for creating an API service
 */
export interface ApiServiceConfig {
  // Base configuration for the API client
  clientConfig: ApiClientConfig;
  // Whether to enable logging (default: true in development, false in production)
  enableLogging?: boolean;
  // Configuration for notifications
  notificationConfig?: NotificationConfig;
  // Function to handle authentication errors
  onAuthError?: (error: any) => void;
  // Function to get the current state from Redux store
  getState?: () => any;
  // Redux dispatch function
  dispatch?: (action: any) => void;
}

/**
 * API Service
 * Provides a configured API client and related utilities
 */
export interface ApiService {
  // The API client instance
  client: ApiClient;
  // The loading state manager
  loadingStateManager: ReturnType<typeof createLoadingStateManager>;
  // Set authentication tokens
  setAuthTokens: (tokens: AuthTokens | null) => void;
  // RTK Query API slice
  apiSlice: ReturnType<typeof createApiSlice>;
}

/**
 * Creates a configured API service
 * @param config - Configuration for the API service
 * @returns An API service instance
 */
export function createApiService(config: ApiServiceConfig): ApiService {
  // Create the API client
  const client = new ApiClient(config.clientConfig);
  
  // Create the loading state manager
  const loadingStateManager = createLoadingStateManager();
  
  // Add loading state handler
  client.addLoadingStateChangeHandler(createGlobalLoadingStateHandler(loadingStateManager));
  
  // Add auth interceptors
  if (config.getState) {
    client.addRequestInterceptor(createAuthRequestInterceptor(config.getState));
  }
  if (config.dispatch) {
    client.addErrorInterceptor(createAuthErrorInterceptor(config.dispatch, config.onAuthError));
  }
  
  // Add logging interceptors if enabled
  const enableLogging = config.enableLogging ?? process.env.NODE_ENV !== 'production';
  if (enableLogging) {
    client.addRequestInterceptor(createRequestLoggingInterceptor());
    client.addResponseInterceptor(createResponseLoggingInterceptor());
    client.addErrorInterceptor(createErrorLoggingInterceptor());
  }
  
  // Add notification handlers if configured
  if (config.notificationConfig) {
    client.addSuccessNotificationHandler(createSuccessNotificationHandler(config.notificationConfig));
    client.addErrorNotificationHandler(createErrorNotificationHandler(config.notificationConfig));
  }
  
  // Create RTK Query API slice
  const apiSlice = createApiSlice(client, config.clientConfig.baseURL);
  
  return {
    client,
    loadingStateManager,
    setAuthTokens: (tokens) => { client.setAuthTokens(tokens); },
    apiSlice,
  };
}

/**
 * Default API service instance
 * This is a placeholder that should be initialized in the application
 */
let defaultApiService: ApiService | null = null;

/**
 * Initializes the default API service
 * @param config - Configuration for the API service
 * @returns The initialized API service
 */
export function initializeApiService(config: ApiServiceConfig): ApiService {
  defaultApiService = createApiService(config);
  return defaultApiService;
}

/**
 * Gets the default API service
 * @returns The default API service
 * @throws Error if the API service has not been initialized
 */
export function getApiService(): ApiService {
  if (!defaultApiService) {
    throw new Error('API service has not been initialized. Call initializeApiService first.');
  }
  return defaultApiService;
}
